import React, { forwardRef, useState, useEffect, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import Input, { InputProps } from "./input";
import useTranslation from "@/hooks/use-translation";

// Password strength calculation
export const strengthIndicator = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 1;
  if (password.length >= 12) strength += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
  if (/\d/.test(password)) strength += 1;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
  return Math.min(strength, 5);
};

const strengthColor = (level: number): { color: string; labelKey: string } => {
  switch (level) {
    case 0:
    case 1:
      return { color: "var(--arc-error-main, #EF4444)", labelKey: "password_strength_weak" };
    case 2:
      return { color: "var(--arc-warning-main, #F59E0B)", labelKey: "password_strength_fair" };
    case 3:
      return { color: "var(--arc-warning-dark, #EAB308)", labelKey: "password_strength_good" };
    case 4:
      return { color: "var(--arc-success-main, #22C55E)", labelKey: "password_strength_strong" };
    case 5:
      return { color: "var(--arc-success-dark, #16A34A)", labelKey: "password_strength_very_strong" };
    default:
      return { color: "var(--arc-content-tertiary, #9CA3AF)", labelKey: "password_strength_weak" };
  }
};

export interface PasswordInputProps extends Omit<InputProps, "type" | "rightIcon"> {
  showStrength?: boolean;
  onStrengthChange?: (level: number) => void;
}

/**
 * Arc-style PasswordInput component
 * 
 * Features:
 * - Password visibility toggle
 * - Optional strength indicator
 * - All Input component features
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      showStrength = false,
      onStrengthChange,
      value,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const [strength, setStrength] = useState({ color: "var(--arc-content-tertiary, #9CA3AF)", labelKey: "password_strength_weak" });
    const stringValue = typeof value === "string" ? value : "";

    useEffect(() => {
      if (onStrengthChange !== undefined || showStrength) {
        const level = strengthIndicator(stringValue);
        setStrength(strengthColor(level));
        onStrengthChange?.(level);
      }
    }, [stringValue, onStrengthChange, showStrength]);

    const toggleButton = (
      <button
        type="button"
        aria-label="toggle password visibility"
        onClick={() => setShowPassword(!showPassword)}
        onMouseDown={(e) => e.preventDefault()}
        className="
          p-1.5
          text-content-tertiary
          hover:text-content-secondary
          transition-colors duration-arc-fast
          rounded-arc-sm
          hover:bg-surface-secondary
          focus:outline-none focus:ring-2 focus:ring-accent
        "
      >
        <FontAwesomeIcon 
          icon={showPassword ? faEye : faEyeSlash} 
          className="w-4 h-4"
        />
      </button>
    );

    return (
      <div className="w-full">
        <Input
          ref={ref}
          type={showPassword ? "text" : "password"}
          value={value}
          rightIcon={toggleButton}
          {...props}
        />
        {showStrength && stringValue !== "" && (
          <div className="mt-2 flex items-center gap-3">
            <div
              className="h-2 rounded-arc-full transition-all duration-arc-normal"
              style={{
                backgroundColor: strength.color,
                width: 85,
              }}
            />
            <span className="text-xs text-content-secondary">
              {t(strength.labelKey)}
            </span>
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;



